import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, CircularProgress, Alert, Pagination } from '@mui/material';
import FoodImageUpload from './FoodImageUpload';
import FoodAnalysisCard from './FoodAnalysisCard';

import {
  addUserFoodAnalysis,
  getUserFoodAnalysis,
  deleteUserFoodAnalysis,
  downloadUserFoodAnalysis,
  getUserFoodAnalysisDetails,
} from '../../../utils/api';
import { useQuery } from 'react-query';
import useStore from '../../../Store/Store';
import axios from 'axios';
import { useRef } from 'react';

const FoodAnalysis = ({ onUploadFood, isUploading = false }) => {
  const { t } = useTranslation();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisDetails, setAnalysisDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const uploadRef = useRef(null);
  const user = useStore((state) => state.user);
  const {
    data: foodAnalysisData,
    isLoading,
    error,
    refetch,
  } = useQuery(['getUserFoodAnalysis', user?._id], () => getUserFoodAnalysis({ userId: user?._id }), {
    retry: false,
    cacheTime: 60000, // Cache data for 1 minute
  });

  const handleUpload = async (file) => {
    if (!user?._id) {
      setUploadError('User not authenticated');
      return;
    }

    setIsProcessing(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      // Get signed URL for file upload
      const { data } = await axios.post(
        `${process.env.REACT_APP_EXPRESS_URL}/api/cancare/user/${user._id}/food/signedUrl`,
        {
          type: file.type,
          name: file.name,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: localStorage.getItem('auth'),
            'ngrok-skip-browser-warning': 'true',
          },
        },
      );

      const postURL = data?.data?.postURL;

      if (!postURL) {
        throw new Error('Failed to get upload URL');
      }

      // Upload file to S3 as binary data
      await axios.put(postURL, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      // Add food analysis record
      await addUserFoodAnalysis({ userId: user._id, body: { name: file.name, link: postURL.split('?')[0] } });

      // Call the parent upload handler if provided
      if (onUploadFood) {
        await onUploadFood(file);
      }

      setUploadSuccess(true);
      // Clear the uploaded image
      if (uploadRef.current) {
        uploadRef.current.clearImage();
      }
      // Reset success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);

      // Refetch the data to show the new analysis
      refetch();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error.response?.data?.message || error.message || t('foodUploadError'));
      // Reset error message after 5 seconds
      setTimeout(() => setUploadError(''), 5000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (analysisId) => {
    try {
      // Add your delete API call here
      await deleteUserFoodAnalysis({ userId: user._id, analysisId });

      // Refetch the data to update the list
      refetch();
    } catch (error) {
      console.error('Delete failed:', error);
      throw error;
    }
  };

  const handleDownload = async (analysis) => {
    try {
      const response = await downloadUserFoodAnalysis({ userId: user._id, analysisId: analysis._id });
      console.log('response =====> ', response);

      if (response?.data) {
        // Create a temporary link element to trigger the download
        const link = document.createElement('a');
        link.href = response.data;
        link.download = `${analysis.mealName || 'food-analysis'}-${new Date(analysis.uploadDate).toISOString().split('T')[0]}.pdf`;
        link.target = '_blank';

        // Append to body, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error('No download link received from API');
      }
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  };

  const handleViewDetails = async (analysis) => {
    const response = await getUserFoodAnalysisDetails({ userId: user._id, analysisId: analysis._id });

    setAnalysisDetails(response.data);
  };

  // foodAnalysisData is the full API response; extract the array from foodAnalysisData.data.analyses
  const analysesArray = foodAnalysisData?.data?.analyses || [];

  // Pagination logic
  const totalPages = Math.ceil(analysesArray.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnalyses = analysesArray.slice(startIndex, endIndex);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  return (
    <Box>
      {/* Upload Section */}

      <FoodImageUpload
        ref={uploadRef}
        onUpload={handleUpload}
        isUploading={isUploading || isProcessing}
        title={t('uploadFoodImage')}
        description={t('uploadFoodImageDescription')}
        uploadButtonText={t('analyzeFoodImage')}
        loadingText={t('analyzingFoodImage')}
        tips={[t('foodTip1'), t('foodTip2'), t('foodTip3'), t('foodTip4')]}
      />

      {/* Success Message */}
      {uploadSuccess && (
        <Alert
          severity="success"
          style={{ marginBottom: '20px', borderRadius: '8px' }}
          onClose={() => setUploadSuccess(false)}
        >
          {t('foodUploadSuccess')}
        </Alert>
      )}

      {/* Error Message */}
      {uploadError && (
        <Alert
          severity="error"
          style={{ marginBottom: '20px', borderRadius: '8px' }}
          onClose={() => setUploadError('')}
        >
          {uploadError}
        </Alert>
      )}

      {/* Analysis Results */}
      {analysesArray?.length > 0 ? (
        <Box>
          <Typography
            variant="h5"
            style={{
              marginBottom: '20px',
              fontWeight: '600',
              color: '#333',
            }}
          >
            {t('analysisHistory')}
          </Typography>

          {/* Completed Analyses */}
          {currentAnalyses.length > 0 && (
            <Box style={{ marginBottom: '32px' }}>
              {currentAnalyses.map((analysis) => (
                <FoodAnalysisCard
                  key={analysis._id}
                  analysis={analysis}
                  onDelete={handleDelete}
                  onDownload={handleDownload}
                  onViewDetails={handleViewDetails}
                  analysisDetails={analysisDetails}
                />
              ))}
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '20px',
                padding: '20px 0',
              }}
            >
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#26786A',
                    '&.Mui-selected': {
                      backgroundColor: '#26786A',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: '#1e5f54',
                      },
                    },
                    '&:hover': {
                      backgroundColor: '#e8f5e8',
                    },
                  },
                }}
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          style={{
            textAlign: 'center',
            padding: '40px',
            background: '#f8f9fa',
            borderRadius: '12px',
            marginTop: '20px',
          }}
        >
          <Typography variant="h6" style={{ color: '#666', marginBottom: '8px' }}>
            {t('noAnalysis')}
          </Typography>
          <Typography variant="body2" style={{ color: '#999' }}>
            {t('uploadPrompt')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FoodAnalysis;
